import type { ClipPreviewAspectRatio } from "./clip-preview-model";

/** In-memory editor source (session only). */
export const CLIP_PREVIEW_EDITOR_SOURCE_MAX_HEIGHT = 720;
export const CLIP_PREVIEW_EDITOR_SOURCE_JPEG_QUALITY = 0.9;

/** Persisted clip_preview / publication thumbnail payload. */
export const CLIP_PREVIEW_PERSISTED_MAX_HEIGHT = 480;
export const CLIP_PREVIEW_PERSISTED_JPEG_QUALITY = 0.8;

export const CLIP_PREVIEW_FRAME_CAPTURE_MAX_ATTEMPTS = 4;

export interface ClipPreviewImageDimensions {
  width: number;
  height: number;
}

/** Width divided by height for a named aspect ratio (e.g. 16:9 → 16/9). */
export function getAspectRatioWidthOverHeight(ratio: ClipPreviewAspectRatio): number {
  switch (ratio) {
    case "9:16":
      return 9 / 16;
    case "1:1":
      return 1;
    case "4:5":
      return 4 / 5;
    case "4:3":
      return 4 / 3;
    case "16:9":
    default:
      return 16 / 9;
  }
}

/**
 * Output canvas size for a fixed max height and target aspect ratio.
 * Width is derived from height so the crop frame matches the ratio exactly.
 */
export function dimensionsForAspectMaxHeight(
  maxHeight: number,
  aspectRatio: ClipPreviewAspectRatio,
): ClipPreviewImageDimensions {
  const height = Math.max(1, Math.round(maxHeight));
  const widthOverHeight = getAspectRatioWidthOverHeight(aspectRatio);
  const width = Math.max(1, Math.round(height * widthOverHeight));
  return { width, height };
}

/**
 * Scales an image down to fit maxHeight while preserving its natural aspect ratio.
 * Never upscales (smaller sources keep native pixel dimensions).
 */
export function dimensionsScaleToMaxHeight(
  naturalWidth: number,
  naturalHeight: number,
  maxHeight: number,
): ClipPreviewImageDimensions {
  if (naturalWidth <= 0 || naturalHeight <= 0) {
    return { width: 1, height: 1 };
  }
  if (naturalHeight <= maxHeight) {
    return { width: naturalWidth, height: naturalHeight };
  }
  const scale = maxHeight / naturalHeight;
  return {
    width: Math.max(1, Math.round(naturalWidth * scale)),
    height: maxHeight,
  };
}

export function loadImageElement(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Image load failed."));
    image.src = src;
  });
}

function drawImageToCanvas(
  image: HTMLImageElement,
  width: number,
  height: number,
): HTMLCanvasElement | null {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(image, 0, 0, width, height);
  return canvas;
}

export async function encodeImageDataUrl(args: {
  source: string | HTMLImageElement;
  maxHeight: number;
  jpegQuality: number;
}): Promise<string | null> {
  try {
    const image =
      typeof args.source === "string" ? await loadImageElement(args.source) : args.source;
    const { width, height } = dimensionsScaleToMaxHeight(
      image.naturalWidth,
      image.naturalHeight,
      args.maxHeight,
    );
    const canvas = drawImageToCanvas(image, width, height);
    if (!canvas) return null;
    return canvas.toDataURL("image/jpeg", args.jpegQuality);
  } catch {
    return null;
  }
}

export async function encodeClipPreviewEditorSourceDataUrl(
  source: string | null | undefined,
): Promise<string | null> {
  const trimmed = source?.trim();
  if (!trimmed) return null;
  return encodeImageDataUrl({
    source: trimmed,
    maxHeight: CLIP_PREVIEW_EDITOR_SOURCE_MAX_HEIGHT,
    jpegQuality: CLIP_PREVIEW_EDITOR_SOURCE_JPEG_QUALITY,
  });
}

export async function encodeClipPreviewPersistedDataUrl(
  source: string | null | undefined,
): Promise<string | null> {
  const trimmed = source?.trim();
  if (!trimmed) return null;
  return encodeImageDataUrl({
    source: trimmed,
    maxHeight: CLIP_PREVIEW_PERSISTED_MAX_HEIGHT,
    jpegQuality: CLIP_PREVIEW_PERSISTED_JPEG_QUALITY,
  });
}

export async function retryAsync<T>(
  fn: () => Promise<T | null | undefined>,
  maxAttempts: number,
  delayBeforeAttemptMs: (attemptIndex: number) => number,
): Promise<T | null> {
  const attempts = Math.max(1, maxAttempts);
  for (let i = 0; i < attempts; i++) {
    if (i > 0) {
      await new Promise((resolve) => setTimeout(resolve, delayBeforeAttemptMs(i)));
    }
    const result = await fn();
    if (result) return result;
  }
  return null;
}

/**
 * Captures a video frame with retry, normalizes to editor-source encoding, or returns fallback preview.
 */
export async function resolveVideoSnapshotEditorSource(args: {
  captureFrame: () => Promise<string | null>;
  fallbackPreviewBase64: string | null;
}): Promise<string | null> {
  const nativeFrame = await retryAsync(
    args.captureFrame,
    CLIP_PREVIEW_FRAME_CAPTURE_MAX_ATTEMPTS,
    (attemptIndex) => attemptIndex * 1000,
  );
  if (nativeFrame) {
    const encoded = await encodeClipPreviewEditorSourceDataUrl(nativeFrame);
    if (encoded) return encoded;
  }
  return args.fallbackPreviewBase64;
}

export interface RenderEditorCropArgs {
  sourceUrl: string;
  aspectRatio: ClipPreviewAspectRatio;
  editorZoom: number;
  editorOffsetX: number;
  editorOffsetY: number;
  viewportWidth: number;
  viewportHeight: number;
  sourceNaturalWidth: number;
  sourceNaturalHeight: number;
  maxOutputHeight: number;
  jpegQuality: number;
}

/**
 * Renders the thumbnail editor crop (zoom/offset) to a JPEG data URL at the target aspect ratio.
 */
export async function renderEditorCropToDataUrl(
  args: RenderEditorCropArgs,
): Promise<string | null> {
  const image = await loadImageElement(args.sourceUrl);
  const { width: outputWidth, height: outputHeight } = dimensionsForAspectMaxHeight(
    args.maxOutputHeight,
    args.aspectRatio,
  );

  const viewportWidth = Math.max(1, Math.round(args.viewportWidth || outputWidth));
  const viewportHeight = Math.max(1, Math.round(args.viewportHeight || outputHeight));

  const naturalWidth = args.sourceNaturalWidth || image.naturalWidth;
  const naturalHeight = args.sourceNaturalHeight || image.naturalHeight;
  if (!naturalWidth || !naturalHeight) return null;

  const baseScale = Math.max(
    viewportWidth / naturalWidth,
    viewportHeight / naturalHeight,
  );
  const zoomScale = args.editorZoom / 100;
  const drawWidth = naturalWidth * baseScale * zoomScale;
  const drawHeight = naturalHeight * baseScale * zoomScale;
  const drawX = (viewportWidth - drawWidth) / 2 + args.editorOffsetX;
  const drawY = (viewportHeight - drawHeight) / 2 + args.editorOffsetY;

  const outputCanvas = document.createElement("canvas");
  outputCanvas.width = outputWidth;
  outputCanvas.height = outputHeight;
  const ctx = outputCanvas.getContext("2d");
  if (!ctx) return null;

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, outputWidth, outputHeight);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  const scaleX = outputWidth / viewportWidth;
  const scaleY = outputHeight / viewportHeight;

  ctx.drawImage(
    image,
    drawX * scaleX,
    drawY * scaleY,
    drawWidth * scaleX,
    drawHeight * scaleY,
  );

  return outputCanvas.toDataURL("image/jpeg", args.jpegQuality);
}
